import { Button } from "@etudescarre/ui";
import {
  CaretLeft as CaretLeftIcon,
  CaretRight as CaretRightIcon,
} from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import { BordereauPrestaGrid } from "../components/diffusion/bordereau-presta-grid";
import { DiffusionQueuePage } from "./diffusion-queue-page";

export type DiffusionScreenProps = {
  title: string;
  target: "do" | "presta";
};

/**
 * Screens 019 "Diffuser vers donneurs d'ordre" and 020 "Diffuser vers
 * prestataires".
 *
 * Left, the waiting list "Documents à diffuser" (document versions the user
 * pilots, put in diffusion toward the target): select documents, remove them
 * from the list, or send them to the bordereau en construction while keeping
 * them in the list or not. Right, that bordereau as a documents × recipients
 * matrix (formats, comment, mode d'envoi, recipient adder, annuler/rétablir,
 * "Enregistrer le brouillon", "Valider le bordereau"); removing a document
 * from it sends it back to the waiting list.
 *
 * The waiting list can be folded away to give the matrix the full width.
 *
 * Deliberately NOT delivered (no representable pipeline yet): the
 * validated bordereau PDF, the recipient/team-members/author e-mail, the
 * assistants-travaux courrier notification, and the notifications-centre entry.
 * Validation only performs the modelled state flip.
 */
export function DiffusionScreen({ title, target }: DiffusionScreenProps) {
  const [listOpen, setListOpen] = useState(true);
  const [bordereauDirty, setBordereauDirty] = useState(false);
  // Additions are blocked while the bordereau has unsaved edits, so it can be
  // remounted to show them.
  const [bordereauKey, setBordereauKey] = useState(0);
  const [listReloadKey, setListReloadKey] = useState(0);

  const reloadBordereau = useCallback(() => setBordereauKey((k) => k + 1), []);
  const reloadList = useCallback(() => setListReloadKey((k) => k + 1), []);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex shrink-0 items-center gap-3 border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
        <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          {title}
        </h1>
        <Button
          type="button"
          outline
          onClick={() => setListOpen((open) => !open)}
          aria-expanded={listOpen}
        >
          {listOpen ? (
            <CaretLeftIcon size={16} />
          ) : (
            <CaretRightIcon size={16} />
          )}
          {listOpen
            ? "Masquer les documents à diffuser"
            : "Afficher les documents à diffuser"}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Hidden, not unmounted, so a folded list keeps its unsaved draft. */}
        <section
          aria-label="Documents à diffuser"
          hidden={!listOpen}
          className="flex w-[36rem] shrink-0 flex-col border-r border-zinc-200 dark:border-zinc-700"
        >
          <DiffusionQueuePage
            title="Documents à diffuser"
            target={target}
            enableDraftWorkflow
            reloadKey={listReloadKey}
            bordereauDirty={bordereauDirty}
            onBordereauChange={reloadBordereau}
          />
        </section>
        <section
          aria-label="Bordereau en construction"
          className="min-w-0 flex-1"
        >
          <BordereauPrestaGrid
            key={bordereauKey}
            title="Bordereau en construction"
            target={target}
            onDirtyChange={setBordereauDirty}
            onSaved={reloadList}
          />
        </section>
      </div>
    </div>
  );
}
